import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../api/axios';
import styles from './AARView.module.css';
import { ArrowLeft, Calendar, CheckCircle, FileText, User, Users } from 'lucide-react';

interface EventAttendance {
  memberId: number;
  username: string;
  rsvpStatus?: string;
  actuallyAttended: boolean;
  pointsAwarded: number;
}

interface AARData {
  id: number;
  eventTitle: string;
  eventStartDate: string;
  eventEndDate: string;
  reporterUsername: string;
  reportTitle: string;
  reportContent: string;
  eventPointsAwarded: number;
  status: string;
  createdAt: string;
  finalizedAt?: string;
  attendances: EventAttendance[];
}

const AARView: React.FC = () => {
  const navigate = useNavigate();
  const { aarId } = useParams();
  const [loading, setLoading] = useState(true);
  const [aarData, setAARData] = useState<AARData | null>(null);

  useEffect(() => {
    fetchAAR();
  }, [aarId]);

  const fetchAAR = async () => {
    try {
      const response = await axios.get(`/afteractionreports/${aarId}`);
      setAARData(response.data);
    } catch (error) {
      console.error('Error fetching AAR:', error);
      alert('Failed to load After Action Report');
      navigate('/after-action-reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!aarData) {
    return null;
  }

  const attendeeCount = aarData.attendances.filter(a => a.actuallyAttended).length;
  const totalPointsAwarded = attendeeCount * aarData.eventPointsAwarded;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/after-action-reports')}>
          <ArrowLeft size={20} />
          Back to Reports
        </button>
        <div className={styles.statusBadge}>
          <CheckCircle size={16} />
          Finalized
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.titleSection}>
          <FileText className={styles.titleIcon} size={32} />
          <h1 className={styles.title}>{aarData.reportTitle}</h1>
        </div>

        <div className={styles.metaSection}>
          <div className={styles.metaGrid}>
            <div className={styles.metaItem}>
              <Calendar className={styles.metaIcon} size={18} />
              <div>
                <div className={styles.metaLabel}>Event Date</div>
                <div className={styles.metaValue}>{new Date(aarData.eventStartDate).toLocaleDateString()}</div>
              </div>
            </div>

            <div className={styles.metaItem}>
              <FileText className={styles.metaIcon} size={18} />
              <div>
                <div className={styles.metaLabel}>Event Title</div>
                <div className={styles.metaValue}>{aarData.eventTitle}</div>
              </div>
            </div>

            <div className={styles.metaItem}>
              <User className={styles.metaIcon} size={18} />
              <div>
                <div className={styles.metaLabel}>Reported By</div>
                <div className={styles.metaValue}>{aarData.reporterUsername}</div>
              </div>
            </div>

            <div className={styles.metaItem}>
              <CheckCircle className={styles.metaIcon} size={18} />
              <div>
                <div className={styles.metaLabel}>Finalized</div>
                <div className={styles.metaValue}>
                  {aarData.finalizedAt ? new Date(aarData.finalizedAt).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Report</h2>
          <div className={styles.reportContent}>
            {aarData.reportContent.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <Users size={24} />
              Attendance & Points
            </h2>
            <div className={styles.pointsSummary}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Attendees:</span>
                <span className={styles.summaryValue}>{attendeeCount}</span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Points per attendee:</span>
                <span className={styles.summaryValue}>{aarData.eventPointsAwarded}</span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Total awarded:</span>
                <span className={styles.summaryValueHighlight}>{totalPointsAwarded}</span>
              </div>
            </div>
          </div>

          <div className={styles.attendanceGrid}>
            {aarData.attendances
              .filter(a => a.actuallyAttended)
              .map((attendance, index) => (
                <div key={index} className={styles.attendanceCard}>
                  <div className={styles.attendeeName}>
                    <CheckCircle size={16} className={styles.checkIcon} />
                    {attendance.username}
                  </div>
                  <div className={styles.attendeeDetails}>
                    {attendance.rsvpStatus && (
                      <span className={styles.rsvpBadge}>RSVP: {attendance.rsvpStatus}</span>
                    )}
                    {!attendance.rsvpStatus && (
                      <span className={styles.walkInBadge}>Walk-in</span>
                    )}
                    <span className={styles.pointsBadge}>+{attendance.pointsAwarded} pts</span>
                  </div>
                </div>
              ))}
          </div>

          {aarData.attendances.some(a => !a.actuallyAttended) && (
            <div className={styles.noShowSection}>
              <h3 className={styles.noShowTitle}>No-Shows / Did Not Attend</h3>
              <div className={styles.noShowList}>
                {aarData.attendances
                  .filter(a => !a.actuallyAttended)
                  .map((attendance, index) => (
                    <div key={index} className={styles.noShowItem}>
                      {attendance.username}
                      {attendance.rsvpStatus && (
                        <span className={styles.noShowBadge}>RSVP: {attendance.rsvpStatus}</span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AARView;
